<script setup>
// NOTICE: KAPTION-INTEGRATION
// ai-connectors.CONSENT: the role-based consent page an AI tool (Claude,
// ChatGPT, Notion, …) sends people to when they connect Rumo. Same OAuth
// contract as the legacy page (signed `oreq` in, grant redirect out), but:
//   CONSENT.1  WhatsApp verification happens here, inline, when missing.
//   CONSENT.2  Step 1: pick roles (cards). Step 2: accounts, all or a
//              multi-select list. Admin-only roles are greyed out, with a
//              hover explanation, for people who aren't an admin anywhere.
//   CONSENT.3  A live table shows exactly what the tool gets, and where.
//   CONSENT.4  Step 3: a fresh WhatsApp code confirms every connection.
//   two-step-verification.WA.3  With the multi-method feature on, step 3
//              accepts any method the person set up (passkey, authenticator
//              app, WhatsApp), preferred first. Without it: unchanged.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';
import kaptionWhatsapp2fa from 'dashboard/api/kaptionWhatsapp2fa';
import kaptionMcpGrant from 'dashboard/api/kaptionMcpGrant';
import KaptionAiClientLogo from './KaptionAiClientLogo.vue';
import { matchAiClient } from './kaptionAiClients';
import { permissionRows, areasForRole } from './kaptionAiPermissions';
import {
  getPasskey,
  isPasskeyCancel,
  isPasskeyUsableHere,
} from 'shared/helpers/kaptionWebauthn';

const props = defineProps({
  oreq: { type: String, required: true },
  clientName: { type: String, required: true },
  twoFaVerified: { type: Boolean, default: false },
  // two-step-verification.WA.3 — empty when the multi-method feature is off.
  twoStepMethods: { type: Array, default: () => [] },
  preferredMethod: { type: String, default: '' },
  // two-step-verification.PASSKEY.3 — passkeys only on a host inside the RP ID
  passkeyRpId: { type: String, default: '' },
});

const { t } = useI18n();

// Display order. `viewer` stays valid server-side but Analyst covers it here.
// NOTICE: KAPTION-INTEGRATION - app-studio.CONSENT.1: `app_builder` is offered
// only when App Studio is allowed for the person in at least one account.
const ROLE_ORDER = [
  'support',
  'marketer',
  'contacts_manager',
  'analyst',
  'assistant_builder',
  'app_builder',
  'system_setup',
  'people_manager',
  'administrator',
];
const ROLE_ICONS = {
  support: 'i-lucide-headset',
  marketer: 'i-lucide-megaphone',
  contacts_manager: 'i-lucide-contact',
  analyst: 'i-lucide-chart-column',
  assistant_builder: 'i-lucide-sparkles',
  system_setup: 'i-lucide-settings-2',
  people_manager: 'i-lucide-users',
  administrator: 'i-lucide-shield-check',
  app_builder: 'i-lucide-blocks',
};

// NOTICE: KAPTION-INTEGRATION - ai-connectors.CONSENT.5: icons for the steps,
// the access levels and where access applies.
const STEP_ICONS = {
  roles: 'i-lucide-user-round-check',
  accounts: 'i-lucide-building-2',
  code: 'i-lucide-message-circle',
};
const ACCESS_ICONS = {
  change: 'i-lucide-pencil',
  read: 'i-lucide-eye',
};
const WHERE_ICONS = {
  everywhere: 'i-lucide-globe',
  admin_accounts: 'i-lucide-shield-check',
};

const client = computed(() => matchAiClient(props.clientName));
const clientLabel = computed(() => client.value?.name || props.clientName);

const step = ref(props.twoFaVerified ? 'loading' : 'twofa'); // twofa | loading | roles | accounts | code | redirecting
const WIZARD = ['roles', 'accounts', 'code'];
const busy = ref(false);
const rolesCatalog = ref({});
const adminOnlyScopes = ref([]);
const accountsCatalog = ref([]);
const authorizeCompleteUrl = ref('');
const selectedRoles = ref(new Set());
const accountMode = ref('all'); // all | choose
const chosenAccountIds = ref(new Set());
const accountMenuOpen = ref(false);
const codeInput = ref('');
const maskedPhone = ref('');

// two-step-verification.WA.3 — which proof confirms the connection.
const confirmMethods = computed(() =>
  props.twoStepMethods.filter(
    m => m !== 'passkey' || isPasskeyUsableHere(props.passkeyRpId)
  )
);
const multiMethod = computed(() => confirmMethods.value.length > 0);
const confirmMethod = ref(
  confirmMethods.value.includes(props.preferredMethod)
    ? props.preferredMethod
    : confirmMethods.value[0] || 'whatsapp'
);
const otherConfirmMethods = computed(() =>
  confirmMethods.value.filter(m => m !== confirmMethod.value)
);
const confirmMethodLabel = method => {
  if (method === 'passkey') return t('KAPTION_TWO_STEP.METHODS.PASSKEY.TITLE');
  if (method === 'authenticator')
    return t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.TITLE');
  return t('KAPTION_TWO_STEP.METHODS.WHATSAPP.TITLE');
};
const CONFIRM_ICONS = {
  passkey: 'i-lucide-key-round',
  authenticator: 'i-lucide-smartphone',
  whatsapp: 'i-lucide-message-circle',
};

// Inline WhatsApp enrollment (CONSENT.1)
const phoneInput = ref('');
const enrollCode = ref('');
const enrollSent = ref(false);
const cooldownLeft = ref(0);
let cooldownTimer = null;

const errorMessage = e =>
  e?.response?.data?.error || t('KAPTION_AI_CONNECTORS.ERROR');

const startCooldown = () => {
  cooldownLeft.value = 60;
  clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    cooldownLeft.value -= 1;
    if (cooldownLeft.value <= 0) clearInterval(cooldownTimer);
  }, 1000);
};
onBeforeUnmount(() => clearInterval(cooldownTimer));

const loadScopes = async () => {
  step.value = 'loading';
  try {
    const { data } = await kaptionMcpGrant.getScopes();
    rolesCatalog.value = data.roles || {};
    adminOnlyScopes.value = data.admin_only_scopes || [];
    accountsCatalog.value = data.accounts || [];
    authorizeCompleteUrl.value = data.authorize_complete_url;
    if (accountsCatalog.value.length === 1) accountMode.value = 'all';
    step.value = 'roles';
  } catch (e) {
    if (e?.response?.data?.error_code === 'whatsapp_2fa_required') {
      step.value = 'twofa';
    } else {
      useAlert(errorMessage(e));
    }
  }
};

onMounted(async () => {
  if (props.twoFaVerified) {
    await loadScopes();
    return;
  }
  try {
    const { data } = await kaptionWhatsapp2fa.getStatus();
    phoneInput.value = data.prefill_phone || data.phone || '';
  } catch (e) {
    // the person types their number
  }
});

const sendEnrollCode = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionWhatsapp2fa.sendCode(phoneInput.value);
    if (data?.phone) phoneInput.value = data.phone;
    enrollSent.value = true;
    startCooldown();
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

const verifyEnrollCode = async () => {
  busy.value = true;
  try {
    await kaptionWhatsapp2fa.verify(enrollCode.value);
    await loadScopes();
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

// Roles (CONSENT.2)
const roleKey = key => key.toUpperCase();
// An admin-only role can't do anything for someone who isn't an administrator
// in any of their accounts, so for them it is greyed out (with a tooltip)
// instead of labelled "Admins only".
const isAdminAnywhere = computed(() =>
  accountsCatalog.value.some(a => a.role === 'administrator')
);
const canUseAppStudioAnywhere = computed(() =>
  accountsCatalog.value.some(a => a.app_studio)
);
const isRoleOffered = key =>
  !!rolesCatalog.value[key] &&
  (key !== 'app_builder' || canUseAppStudioAnywhere.value);
const roleCards = computed(() =>
  ROLE_ORDER.filter(isRoleOffered).map(key => {
    const adminOnly = !!rolesCatalog.value[key].admin_only;
    const locked = adminOnly && !isAdminAnywhere.value;
    return {
      key,
      showAdminBadge: adminOnly && isAdminAnywhere.value,
      locked,
      disabled: locked,
    };
  })
);
const cardTooltip = card => {
  if (card.locked) return t('KAPTION_AI_CONNECTORS.CONSENT.ADMIN_ONLY_TIP');
  return undefined;
};

const toggleRole = card => {
  if (card.disabled) return;
  const next = new Set(selectedRoles.value);
  if (next.has(card.key)) next.delete(card.key);
  else next.add(card.key);
  selectedRoles.value = next;
};

// Accounts (CONSENT.2)
const coveredAccounts = computed(() =>
  accountMode.value === 'all'
    ? accountsCatalog.value
    : accountsCatalog.value.filter(a => chosenAccountIds.value.has(a.id))
);

const toggleAccount = id => {
  const next = new Set(chosenAccountIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  chosenAccountIds.value = next;
};

const accountMenuLabel = computed(() => {
  const chosen = coveredAccounts.value;
  if (!chosen.length)
    return t('KAPTION_AI_CONNECTORS.CONSENT.SELECT_PLACEHOLDER');
  if (chosen.length <= 2) return chosen.map(a => a.name).join(', ');
  return t('KAPTION_AI_CONNECTORS.CONSENT.SELECTED', { count: chosen.length });
});

// Exactly what the tool gets (CONSENT.3)
const rows = computed(() =>
  permissionRows({
    roleKeys: [...selectedRoles.value],
    rolesCatalog: rolesCatalog.value,
    adminOnlyScopes: adminOnlyScopes.value,
    accounts: coveredAccounts.value,
    // NOTICE: KAPTION-INTEGRATION - app-studio.CONSENT.1: no Apps row for
    // someone who can't use App Studio anywhere.
  }).filter(row => row.key !== 'APPS' || canUseAppStudioAnywhere.value)
);

const accessLabel = level => {
  if (level === 'change') return t('KAPTION_AI_CONNECTORS.CONSENT.CHANGE');
  if (level === 'read') return t('KAPTION_AI_CONNECTORS.CONSENT.READ');
  return t('KAPTION_AI_CONNECTORS.CONSENT.NONE');
};

// ai-connectors.CONSENT.5: the areas each role opens, as icon chips.
const roleAreas = computed(() =>
  Object.fromEntries(
    roleCards.value.map(card => [
      card.key,
      areasForRole(card.key, rolesCatalog.value).filter(
        area => area.key !== 'APPS' || canUseAppStudioAnywhere.value
      ),
    ])
  )
);
const areaTitle = area =>
  `${t(`KAPTION_AI_CONNECTORS.CONSENT.AREAS.${area.key}`)} · ${accessLabel(area.level)}`;

const names = list => list.map(a => a.name).join(', ');

const whereLabel = row => {
  if (row.where === 'nowhere') return '—';
  if (row.where === 'admin_accounts') {
    const key = row.agentLevel
      ? 'KAPTION_AI_CONNECTORS.CONSENT.WHERE_ADMIN_ELSE'
      : 'KAPTION_AI_CONNECTORS.CONSENT.WHERE_ADMIN';
    return t(key, { accounts: names(row.adminAccounts) });
  }
  return t('KAPTION_AI_CONNECTORS.CONSENT.WHERE_ALL');
};

// Roles that won't apply in accounts where the person is an agent.
const agentOnlyNote = computed(() => {
  const agentAccounts = coveredAccounts.value.filter(
    a => a.role !== 'administrator'
  );
  const adminRoles = [...selectedRoles.value].filter(
    key => rolesCatalog.value[key]?.admin_only
  );
  if (!agentAccounts.length || !adminRoles.length) return '';
  return t('KAPTION_AI_CONNECTORS.CONSENT.NOT_HERE', {
    accounts: names(agentAccounts),
    roles: adminRoles
      .map(key => t(`KAPTION_AI_CONNECTORS.ROLES.${roleKey(key)}.NAME`))
      .join(', '),
  });
});

const canGoToAccounts = computed(() => selectedRoles.value.size > 0);
const canContinue = computed(
  () => selectedRoles.value.size > 0 && coveredAccounts.value.length > 0
);
const wizardIndex = computed(() => WIZARD.indexOf(step.value));

// Confirm with a fresh code (CONSENT.4)
const requestCode = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionMcpGrant.requestCode();
    maskedPhone.value = data?.phone || '';
    codeInput.value = '';
    step.value = 'code';
    startCooldown();
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

// two-step-verification.WA.3 — open step 3 with the chosen method.
const startConfirm = async () => {
  if (!multiMethod.value || confirmMethod.value === 'whatsapp') {
    await requestCode();
    return;
  }
  codeInput.value = '';
  step.value = 'code';
};

const switchConfirmMethod = async method => {
  confirmMethod.value = method;
  codeInput.value = '';
  if (method === 'whatsapp') await requestCode();
};

const approve = async (proof = {}) => {
  busy.value = true;
  const all = accountMode.value === 'all';
  try {
    const { data } = await kaptionMcpGrant.createGrant({
      ...(multiMethod.value ? { method: confirmMethod.value } : {}),
      ...proof,
      accountId: coveredAccounts.value[0]?.id ?? null,
      accountIds: all ? [] : coveredAccounts.value.map(a => a.id),
      allAccounts: all,
      roles: [...selectedRoles.value],
      scopes: [],
      code: codeInput.value,
      oreq: props.oreq,
      clientName: props.clientName,
    });
    step.value = 'redirecting';
    window.location.assign(data.redirect_uri);
  } catch (e) {
    useAlert(errorMessage(e));
    busy.value = false;
  }
};

const approveWithPasskey = async () => {
  busy.value = true;
  try {
    const { data: options } = await kaptionMcpGrant.passkeyOptions();
    const passkeyCredential = await getPasskey(options);
    await approve({ passkeyCredential });
  } catch (e) {
    useAlert(
      isPasskeyCancel(e)
        ? t('KAPTION_TWO_STEP.LOGIN.ERRORS.PASSKEY_CANCELLED')
        : errorMessage(e)
    );
    busy.value = false;
  }
};

const deny = () => {
  if (authorizeCompleteUrl.value) {
    window.location.assign(
      `${authorizeCompleteUrl.value}?oreq=${encodeURIComponent(props.oreq)}&error=access_denied`
    );
  } else {
    window.close();
  }
};
</script>

<template>
  <div
    class="flex items-start justify-center w-full min-h-screen bg-n-background pt-[8vh] pb-16 px-4"
  >
    <div class="w-full max-w-2xl">
      <div
        class="bg-n-solid-1 rounded-xl border border-n-weak shadow-sm"
        data-testid="ai-consent"
      >
        <!-- Header: who is asking -->
        <div class="flex items-center gap-3 px-6 py-5 border-b border-n-weak">
          <KaptionAiClientLogo :client-id="client?.id || ''" size="lg" />
          <Icon
            icon="i-lucide-arrow-left-right"
            class="flex-shrink-0 size-4 text-n-slate-10"
          />
          <span
            class="flex items-center justify-center flex-shrink-0 border size-12 rounded-xl border-n-weak bg-n-solid-1"
            aria-hidden="true"
          >
            <Logo class="size-7" />
          </span>
          <div class="min-w-0 ltr:ml-1 rtl:mr-1">
            <h1 class="text-lg font-medium truncate text-n-slate-12">
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.TITLE', {
                  client: clientLabel,
                })
              }}
            </h1>
            <p class="text-sm text-n-slate-11">
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.SUBTITLE', {
                  client: clientLabel,
                })
              }}
            </p>
          </div>
        </div>

        <!-- Steps: Roles · Accounts · Confirm -->
        <ol
          v-if="wizardIndex >= 0"
          class="flex items-center gap-2 px-6 pt-4 m-0 text-xs list-none"
          data-testid="ai-consent-steps"
        >
          <template v-for="(name, i) in WIZARD" :key="name">
            <li
              class="flex items-center gap-1.5 list-none"
              :class="i <= wizardIndex ? 'text-n-slate-12' : 'text-n-slate-10'"
              :aria-current="i === wizardIndex ? 'step' : undefined"
            >
              <span
                class="flex items-center justify-center rounded-full size-6 text-[11px] font-medium"
                :class="
                  i < wizardIndex
                    ? 'bg-n-brand text-white'
                    : i === wizardIndex
                      ? 'border border-n-brand text-n-brand'
                      : 'border border-n-weak'
                "
              >
                <Icon
                  :icon="i < wizardIndex ? 'i-lucide-check' : STEP_ICONS[name]"
                  class="size-3"
                />
              </span>
              {{
                $t(`KAPTION_AI_CONNECTORS.CONSENT.STEP_${name.toUpperCase()}`)
              }}
            </li>
            <li
              v-if="i < WIZARD.length - 1"
              aria-hidden="true"
              class="flex-1 h-px list-none max-w-12 bg-n-weak"
            />
          </template>
        </ol>

        <div class="px-6 py-5">
          <!-- CONSENT.1: inline WhatsApp verification -->
          <div
            v-if="step === 'twofa'"
            class="flex flex-col gap-4"
            data-testid="ai-consent-twofa"
          >
            <div class="flex items-start gap-3">
              <span
                class="flex items-center justify-center flex-shrink-0 rounded-lg size-9 bg-n-alpha-black2"
              >
                <Icon
                  icon="i-lucide-shield-check"
                  class="size-4 text-n-brand"
                />
              </span>
              <div class="flex flex-col min-w-0 gap-0.5">
                <h2 class="text-base font-medium text-n-slate-12">
                  {{ $t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.TITLE') }}
                </h2>
                <p class="text-sm text-n-slate-11">
                  {{ $t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.DESC') }}
                </p>
              </div>
            </div>
            <div class="flex items-end gap-2">
              <div class="flex-1 min-w-0">
                <Input
                  id="ai-2fa-phone"
                  v-model="phoneInput"
                  type="tel"
                  :label="$t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.PHONE')"
                  placeholder="+54 9 11 5555 0199"
                />
              </div>
              <Button
                type="button"
                class="flex-shrink-0"
                :label="
                  cooldownLeft > 0
                    ? $t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.RESEND_IN', {
                        seconds: cooldownLeft,
                      })
                    : $t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.SEND')
                "
                :disabled="busy || cooldownLeft > 0 || !phoneInput"
                data-testid="ai-2fa-send"
                @click="sendEnrollCode"
              />
            </div>
            <div v-if="enrollSent" class="flex items-end gap-2">
              <div class="flex-1 min-w-0">
                <Input
                  id="ai-2fa-code"
                  v-model="enrollCode"
                  inputmode="numeric"
                  maxlength="6"
                  autocomplete="one-time-code"
                  :label="$t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.CODE')"
                  custom-input-class="font-mono tracking-[0.3em]"
                />
              </div>
              <Button
                type="button"
                class="flex-shrink-0"
                :label="$t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.VERIFY')"
                :disabled="busy || enrollCode.length !== 6"
                data-testid="ai-2fa-verify"
                @click="verifyEnrollCode"
              />
            </div>
          </div>

          <div v-else-if="step === 'loading'" class="text-sm text-n-slate-11">
            {{ $t('KAPTION_MCP_CONSENT.LOADING') }}
          </div>

          <!-- CONSENT.2 step 1: roles -->
          <template v-else-if="step === 'roles'">
            <h2 class="text-base font-medium text-n-slate-12">
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.ROLES_TITLE', {
                  client: clientLabel,
                })
              }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ $t('KAPTION_AI_CONNECTORS.CONSENT.ROLES_HINT') }}
            </p>
            <div class="grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2">
              <button
                v-for="card in roleCards"
                :key="card.key"
                type="button"
                role="checkbox"
                :aria-checked="selectedRoles.has(card.key)"
                :aria-disabled="card.disabled"
                :title="cardTooltip(card)"
                :data-testid="`ai-role-${card.key}`"
                class="flex items-start gap-3 p-3 text-left transition-colors border rounded-lg"
                :class="[
                  selectedRoles.has(card.key)
                    ? 'border-n-brand bg-n-alpha-black2'
                    : 'border-n-weak',
                  card.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-n-alpha-black2',
                ]"
                @click="toggleRole(card)"
              >
                <span
                  class="flex items-center justify-center flex-shrink-0 rounded-lg size-8 bg-n-alpha-black2"
                >
                  <Icon
                    :icon="card.locked ? 'i-lucide-lock' : ROLE_ICONS[card.key]"
                    class="size-4 text-n-slate-11"
                  />
                </span>
                <span class="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span class="flex flex-wrap items-center gap-1.5">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          `KAPTION_AI_CONNECTORS.ROLES.${roleKey(card.key)}.NAME`
                        )
                      }}
                    </span>
                    <span
                      v-if="card.showAdminBadge"
                      class="px-1.5 py-0.5 text-[11px] leading-none rounded bg-n-amber-3 text-n-amber-11"
                    >
                      {{ $t('KAPTION_AI_CONNECTORS.CONSENT.ADMINS_ONLY') }}
                    </span>
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t(
                        `KAPTION_AI_CONNECTORS.ROLES.${roleKey(card.key)}.DESC`
                      )
                    }}
                  </span>
                  <span
                    class="flex flex-wrap gap-1 mt-1.5"
                    :data-testid="`ai-role-areas-${card.key}`"
                  >
                    <span
                      v-for="area in roleAreas[card.key]"
                      :key="area.key"
                      :title="areaTitle(area)"
                      :aria-label="areaTitle(area)"
                      :data-level="area.level"
                      class="flex items-center gap-0.5 px-1.5 py-1 rounded-md bg-n-alpha-black2"
                      :class="
                        area.level === 'change'
                          ? 'text-n-brand'
                          : 'text-n-slate-11'
                      "
                    >
                      <Icon :icon="area.icon" class="size-3.5" />
                      <Icon
                        v-if="area.level === 'change'"
                        icon="i-lucide-pencil"
                        class="size-3"
                      />
                    </span>
                  </span>
                </span>
                <Icon
                  v-if="selectedRoles.has(card.key)"
                  icon="i-lucide-check"
                  class="flex-shrink-0 size-4 text-n-brand"
                />
              </button>
            </div>
            <p
              class="flex items-center gap-3 mt-3 text-xs text-n-slate-11"
              data-testid="ai-access-legend"
            >
              <span class="flex items-center gap-1">
                <Icon :icon="ACCESS_ICONS.read" class="size-3.5" />
                {{ $t('KAPTION_AI_CONNECTORS.CONSENT.READ') }}
              </span>
              <span class="flex items-center gap-1 text-n-brand">
                <Icon :icon="ACCESS_ICONS.change" class="size-3.5" />
                {{ $t('KAPTION_AI_CONNECTORS.CONSENT.CHANGE') }}
              </span>
            </p>
            <p
              v-if="!isAdminAnywhere"
              class="flex items-start gap-1.5 mt-3 text-xs text-n-slate-11"
              data-testid="ai-locked-note"
            >
              <Icon icon="i-lucide-lock" class="size-3.5 mt-px flex-shrink-0" />
              {{ $t('KAPTION_AI_CONNECTORS.CONSENT.LOCKED_NOTE') }}
            </p>
          </template>

          <!-- CONSENT.2 step 2: accounts + CONSENT.3: what the tool gets -->
          <template v-else-if="step === 'accounts'">
            <h2 class="text-base font-medium text-n-slate-12">
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.ACCOUNTS_TITLE', {
                  client: clientLabel,
                })
              }}
            </h2>
            <div class="flex flex-col gap-2 mt-3">
              <label
                class="flex items-start gap-2.5 p-3 border rounded-lg cursor-pointer"
                :class="
                  accountMode === 'all' ? 'border-n-brand' : 'border-n-weak'
                "
              >
                <input
                  v-model="accountMode"
                  type="radio"
                  value="all"
                  class="mt-0.5"
                  data-testid="ai-accounts-all"
                />
                <Icon
                  icon="i-lucide-layers"
                  class="flex-shrink-0 size-4 mt-0.5 text-n-slate-11"
                />
                <span class="flex flex-col">
                  <span class="text-sm text-n-slate-12">
                    {{ $t('KAPTION_AI_CONNECTORS.CONSENT.ALL') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t('KAPTION_AI_CONNECTORS.CONSENT.ALL_HINT', {
                        count: accountsCatalog.length,
                      })
                    }}
                  </span>
                </span>
              </label>
              <div
                class="flex flex-col gap-2 p-3 border rounded-lg"
                :class="
                  accountMode === 'choose' ? 'border-n-brand' : 'border-n-weak'
                "
              >
                <label class="flex items-start gap-2.5 cursor-pointer">
                  <input
                    v-model="accountMode"
                    type="radio"
                    value="choose"
                    class="mt-0.5"
                    data-testid="ai-accounts-choose"
                  />
                  <Icon
                    icon="i-lucide-list-checks"
                    class="flex-shrink-0 size-4 mt-0.5 text-n-slate-11"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{ $t('KAPTION_AI_CONNECTORS.CONSENT.CHOOSE') }}
                  </span>
                </label>
                <OnClickOutside
                  v-if="accountMode === 'choose'"
                  class="relative"
                  @trigger="accountMenuOpen = false"
                >
                  <button
                    type="button"
                    class="flex items-center justify-between w-full h-10 px-3 text-sm rounded-lg outline outline-1 outline-n-weak -outline-offset-1 bg-n-alpha-black2 text-n-slate-12"
                    data-testid="ai-accounts-menu"
                    :aria-expanded="accountMenuOpen"
                    @click="accountMenuOpen = !accountMenuOpen"
                  >
                    <span class="truncate">{{ accountMenuLabel }}</span>
                    <Icon
                      icon="i-lucide-chevron-down"
                      class="flex-shrink-0 size-4 text-n-slate-11"
                    />
                  </button>
                  <div
                    v-if="accountMenuOpen"
                    class="absolute z-10 w-full mt-1 overflow-y-auto border rounded-lg shadow-lg border-n-weak bg-n-solid-2 max-h-64"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    <label
                      v-for="account in accountsCatalog"
                      :key="account.id"
                      class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-black2"
                      :data-testid="`ai-account-${account.id}`"
                    >
                      <input
                        type="checkbox"
                        :checked="chosenAccountIds.has(account.id)"
                        @change="toggleAccount(account.id)"
                      />
                      <span class="flex-1 truncate">{{ account.name }}</span>
                      <span
                        class="flex items-center gap-1 text-xs text-n-slate-10"
                      >
                        <Icon
                          :icon="
                            account.role === 'administrator'
                              ? 'i-lucide-shield-check'
                              : 'i-lucide-user'
                          "
                          class="size-3"
                        />
                        {{
                          account.role === 'administrator'
                            ? $t('KAPTION_AI_CONNECTORS.CONSENT.YOU_ADMIN')
                            : $t('KAPTION_AI_CONNECTORS.CONSENT.YOU_AGENT')
                        }}
                      </span>
                    </label>
                  </div>
                </OnClickOutside>
              </div>
            </div>

            <h2 class="mt-6 text-base font-medium text-n-slate-12">
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.GETS_TITLE', {
                  client: clientLabel,
                })
              }}
            </h2>
            <div class="mt-2 overflow-x-auto border rounded-lg border-n-weak">
              <table class="w-full text-sm" data-testid="ai-gets-table">
                <thead class="bg-n-alpha-black2">
                  <tr class="text-xs text-left text-n-slate-11">
                    <th class="px-3 py-2 font-medium">
                      {{ $t('KAPTION_AI_CONNECTORS.CONSENT.COL_AREA') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ $t('KAPTION_AI_CONNECTORS.CONSENT.COL_ACCESS') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ $t('KAPTION_AI_CONNECTORS.CONSENT.COL_WHERE') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="row in rows"
                    :key="row.key"
                    :data-testid="`ai-gets-${row.key}`"
                    :class="row.level ? 'text-n-slate-12' : 'text-n-slate-10'"
                  >
                    <td class="px-3 py-2">
                      <span class="flex items-center gap-2">
                        <Icon
                          :icon="row.icon"
                          class="flex-shrink-0 size-4"
                          :class="row.level ? 'text-n-slate-11' : ''"
                        />
                        {{
                          $t(`KAPTION_AI_CONNECTORS.CONSENT.AREAS.${row.key}`)
                        }}
                      </span>
                    </td>
                    <td
                      class="px-3 py-2"
                      :class="{
                        'text-n-brand font-medium': row.level === 'change',
                      }"
                    >
                      <span class="flex items-center gap-1.5 whitespace-nowrap">
                        <Icon
                          :icon="ACCESS_ICONS[row.level] || 'i-lucide-minus'"
                          class="flex-shrink-0 size-3.5"
                        />
                        {{ accessLabel(row.level) }}
                      </span>
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">
                      <span class="flex items-center gap-1.5">
                        <Icon
                          v-if="WHERE_ICONS[row.where]"
                          :icon="WHERE_ICONS[row.where]"
                          class="flex-shrink-0 size-3.5"
                        />
                        {{ whereLabel(row) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              v-if="agentOnlyNote"
              class="flex items-start gap-1.5 mt-2 text-xs text-n-amber-11"
              data-testid="ai-agent-note"
            >
              <Icon icon="i-lucide-info" class="size-3.5 mt-px flex-shrink-0" />
              {{ agentOnlyNote }}
            </p>
            <p class="flex items-start gap-1.5 mt-2 text-xs text-n-slate-11">
              <Icon icon="i-lucide-hand" class="size-3.5 mt-px flex-shrink-0" />
              {{
                $t('KAPTION_AI_CONNECTORS.CONSENT.ASKS', {
                  client: clientLabel,
                })
              }}
            </p>
          </template>

          <!-- CONSENT.4 step 3: fresh code -->
          <div
            v-else-if="step === 'code'"
            class="flex flex-col items-center gap-3 py-4 text-center"
          >
            <span
              class="flex items-center justify-center rounded-full size-11 bg-n-alpha-black2"
            >
              <Icon
                :icon="
                  multiMethod
                    ? CONFIRM_ICONS[confirmMethod]
                    : 'i-lucide-message-circle'
                "
                class="size-5 text-n-brand"
              />
            </span>
            <h2 class="text-base font-medium text-n-slate-12">
              {{
                multiMethod
                  ? $t('KAPTION_TWO_STEP.CONSENT.TITLE')
                  : $t('KAPTION_AI_CONNECTORS.CONSENT.CODE_TITLE')
              }}
            </h2>
            <!-- two-step-verification.WA.3: passkey / authenticator app -->
            <template v-if="multiMethod && confirmMethod === 'passkey'">
              <p class="max-w-sm text-sm text-n-slate-11">
                {{ $t('KAPTION_TWO_STEP.CONSENT.PASSKEY_HINT') }}
              </p>
              <Button
                type="button"
                icon="i-lucide-key-round"
                :label="$t('KAPTION_TWO_STEP.LOGIN.PASSKEY_BUTTON')"
                :is-loading="busy"
                :disabled="busy"
                data-testid="ai-consent-passkey"
                @click="approveWithPasskey"
              />
            </template>
            <template
              v-else-if="multiMethod && confirmMethod === 'authenticator'"
            >
              <p class="max-w-sm text-sm text-n-slate-11">
                {{ $t('KAPTION_TWO_STEP.CONSENT.AUTH_HINT') }}
              </p>
              <Input
                id="ai-consent-otp"
                v-model="codeInput"
                class="w-56"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
                placeholder="••••••"
                data-testid="ai-consent-otp"
                custom-input-class="!h-12 text-center !text-xl font-mono tracking-[0.4em]"
                @enter="codeInput.length === 6 && approve({ code: codeInput })"
              />
            </template>
            <template v-else>
              <p class="max-w-sm text-sm text-n-slate-11">
                {{
                  $t('KAPTION_AI_CONNECTORS.CONSENT.CODE_HINT', {
                    phone: maskedPhone,
                  })
                }}
              </p>
              <Input
                id="ai-consent-code"
                v-model="codeInput"
                class="w-56"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
                placeholder="••••••"
                data-testid="ai-consent-code"
                custom-input-class="!h-12 text-center !text-xl font-mono tracking-[0.4em]"
                @enter="codeInput.length === 6 && approve({ code: codeInput })"
              />
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
                :disabled="busy || cooldownLeft > 0"
                @click="requestCode"
              >
                {{
                  cooldownLeft > 0
                    ? $t('KAPTION_AI_CONNECTORS.CONSENT.TWO_FA.RESEND_IN', {
                        seconds: cooldownLeft,
                      })
                    : $t('KAPTION_AI_CONNECTORS.CONSENT.RESEND')
                }}
              </button>
            </template>
            <div
              v-if="multiMethod && otherConfirmMethods.length"
              class="flex flex-wrap justify-center gap-3 pt-1"
            >
              <button
                v-for="method in otherConfirmMethods"
                :key="method"
                type="button"
                class="text-xs font-medium text-n-brand hover:underline disabled:opacity-50"
                :disabled="busy"
                :data-testid="`ai-consent-switch-${method}`"
                @click="switchConfirmMethod(method)"
              >
                {{
                  $t('KAPTION_TWO_STEP.CONSENT.SWITCH', {
                    method: confirmMethodLabel(method),
                  })
                }}
              </button>
            </div>
          </div>

          <p v-else class="text-sm text-n-slate-11">
            {{ $t('KAPTION_MCP_CONSENT.REDIRECTING', { client: clientLabel }) }}
          </p>
        </div>

        <!-- Footer: Back on the left, Cancel + the step's action on the right -->
        <div
          v-if="step !== 'loading' && step !== 'redirecting'"
          class="flex items-center gap-2 px-6 py-4 border-t border-n-weak"
        >
          <Button
            v-if="step === 'accounts' || step === 'code'"
            type="button"
            slate
            ghost
            icon="i-lucide-arrow-left"
            :label="$t('KAPTION_MCP_CONSENT.BACK')"
            data-testid="ai-consent-back"
            @click="step = step === 'code' ? 'accounts' : 'roles'"
          />
          <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
            <Button
              type="button"
              slate
              ghost
              :label="$t('KAPTION_AI_CONNECTORS.CONSENT.CANCEL')"
              @click="deny"
            />
            <Button
              v-if="step === 'roles'"
              type="button"
              :label="$t('KAPTION_AI_CONNECTORS.CONSENT.NEXT')"
              :disabled="!canGoToAccounts"
              data-testid="ai-consent-next"
              @click="step = 'accounts'"
            />
            <Button
              v-else-if="step === 'accounts'"
              type="button"
              :label="$t('KAPTION_AI_CONNECTORS.CONSENT.CONTINUE')"
              :disabled="busy || !canContinue"
              data-testid="ai-consent-continue"
              @click="startConfirm"
            />
            <Button
              v-else-if="
                step === 'code' && !(multiMethod && confirmMethod === 'passkey')
              "
              type="button"
              :label="$t('KAPTION_MCP_CONSENT.APPROVE')"
              :disabled="busy || codeInput.length !== 6"
              data-testid="ai-consent-approve"
              @click="approve({ code: codeInput })"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
